import React, { Suspense, useEffect } from 'react';
import { Route, Redirect, Switch, useLocation, Link } from 'react-router-dom';
import Cookies from 'universal-cookie';
import { sendGA4Pageview } from 'common/helpers/ga4';
import { identifyAnalyticsUser } from 'common/helpers/analytics';

import * as Sentry from '@sentry/react';

import { map, get } from 'lodash';

import { compose, Activate } from 'common';
import Loading from 'common/components/Loading';
import lazy from 'common/utils/lazy';
import { UserContext } from 'common/context/UserContext';

import withApi from 'common/hocs/withApi';
import withUser from 'common/hocs/withUser';

import Content from 'common/components/Content';
import { useWhoAmI } from './queries/useWhoAmI';
import { ThemeProvider } from 'common';

import logger from 'itrvl-logger';
import { useApi } from 'common/hooks/api';
import { buildTheme } from './utils/theme';
const log = logger(__filename);
log.trace(__filename);

const cookies = new Cookies();

// Custom hook to track user properties across analytics providers when user data changes
// Keyed on primitive IDs so it doesn't re-fire on every render when the user object ref changes
const useUserTracking = user => {
  const userId = user?.id;
  const agencyId = user?.agency?.id;
  useEffect(() => {
    if (user) {
      identifyAnalyticsUser({
        agency: user?.agency,
        agent: user?.agent,
        client: user,
        userType: 'client',
      });
    }
  }, [userId, agencyId]); // eslint-disable-line react-hooks/exhaustive-deps
};

const Config = lazy(() => import('common/components/Config'), { chunkName: 'Config' });

const Enquire = lazy(() => import('./components/enquire/Enquire'), { chunkName: 'Enquire' });
const Enquire2 = lazy(() => import('./components/enquire/Enquire2'), { chunkName: 'Enquire2' });
const Enquire3 = lazy(() => import('./components/enquire/Enquire3'), { chunkName: 'Enquire3' });
const Enquire4 = lazy(() => import('./components/enquire/Enquire4'), { chunkName: 'Enquire4' });
const Country = lazy(() => import('./components/Country'), { chunkName: 'Country' });
const RegionInfo = lazy(() => import('./components/RegionInfo'), { chunkName: 'RegionInfo' });
const ActivityInfo = lazy(() => import('./components/ActivityInfo'), { chunkName: 'ActivityInfo' });
const LoginScreen = lazy(() => import('./components/LoginScreen'), { chunkName: 'LoginScreen' });
const ShareItinerary = lazy(() => import('./components/ShareItinerary'), { chunkName: 'ShareItinerary' });
const ViewIcons = lazy(() => import('common/components/Icons/ViewIcons'), { chunkName: 'ViewIcons' });
const ViewIcons2 = lazy(() => import('common/components/Icons/ViewIcons2'), { chunkName: 'ViewIcons2' });
const PaymentFlow = lazy(() => import('./pages/PaymentFlow'), { chunkName: 'PaymentFlow' });
const Adyen3dsReturn = lazy(() => import('./pages/PaymentFlow/processors/adyen/Adyen3dsReturn'), { chunkName: 'Adyen3dsReturn' });

const Portal = lazy(() => import('./components/Portal'), { chunkName: 'Portal' });

// @todo when all views are ported over remove the determine view logic and just use the renderer directly
const ItinerariesPage = lazy(() => import('./pages/ItinerariesPage.js'), { chunkName: 'ItinerariesPage' });
const ItineraryPage = lazy(() => import('./pages/ItineraryPage.js'), { chunkName: 'ItineraryPage' });
const ExampleItineraryPage = lazy(() => import('./pages/ExampleItineraryPage.js'), { chunkName: 'ExampleItineraryPage' });
const QuestionsPage = lazy(() => import('./pages/QuestionsPage.js'), { chunkName: 'QuestionsPage' });
const CampPage = lazy(() => import('./pages/CampPage.js'), { chunkName: 'CampPage' });
const ProfilePage = lazy(() => import('./pages/ProfilePage.js'), { chunkName: 'ProfilePage' });
const BookingsPage = lazy(() => import('./pages/BookingsPage'), { chunkName: 'BookingsPage' });

const SentryRoute = Sentry.withSentryRouting(Route);

const SentryError = props => {
  const pretag = props.history.action === 'PUSH' ? '' : 'web-';
  return (
    <SentryRoute
      {...props}
      component={() => {
        throw new Error(`throw.sentry.${pretag}client`);
      }}
    />
  );
};

const AuthenticatedRoute = ({ component: Component, userContext, ...rest }) => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const token = location.search !== '' && urlParams.get('token') ? urlParams.get('token') : cookies.get('CLIENT_TOKEN');

  const Api = useApi();
  Api._updateInstanceToken(token);
  const { isLoading, isError, data: user } = useWhoAmI(token);

  useEffect(() => {
    document.title = rest?.name;
    sendGA4Pageview({
      page: location.pathname,
      title: rest?.name,
      contentGroup: rest?.path,
    });
  }, [location, rest?.name, rest?.path]);

  useUserTracking(user);

  if (!token || isError) {
    return (
      <Redirect
        to={{
          pathname: '/client/login',
          state: { from: location },
        }}
      />
    );
  }
  if (isLoading === false) {
    const customizations = get(user, 'agency', {});
    const { theme } = buildTheme(customizations);

    return (
      <SentryRoute
        {...rest}
        render={props =>
          user ? (
            <UserContext.Provider
              value={{
                refreshItineraries: () => {},
                refreshedItinerary: () => {},
                refreshUser: () => {},
                updateClient: () => {},
                user: user,
                client: user,
                updateUser: userContext.updateUser,
                agency: user.agency,
              }}
            >
              <ThemeProvider value={{ theme, customizations }}>
                <Component {...rest} user={user} />
              </ThemeProvider>
            </UserContext.Provider>
          ) : (
            <Redirect
              to={{
                pathname: '/client/login',
                state: { from: props.location },
              }}
            />
          )
        }
      />
    );
  } else {
    return <></>;
  }
};

const NonAuthenticatedRoute = ({ component: Component, userContext, ...rest }) => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const token = location.search !== '' && urlParams.get('token') ? urlParams.get('token') : cookies.get('CLIENT_TOKEN');

  const Api = useApi();
  Api._updateInstanceToken(token);
  const { data: user } = useWhoAmI(token);

  useEffect(() => {
    document.title = rest?.name;
    sendGA4Pageview({
      page: location.pathname,
      title: rest?.name,
      contentGroup: rest?.path,
    });
  }, [location, rest?.name, rest?.path]);

  useUserTracking(user);

  // Compute theme unconditionally so the JSX tree shape stays the same regardless of user state,
  // preventing React from unmounting/remounting components when principalType resolves async
  const customizations = user?.principalType ? get(user, 'agency', {}) : {};
  const { theme } = buildTheme(customizations);

  const contextValue = user?.principalType
    ? {
        refreshItineraries: () => {},
        refreshedItinerary: () => {},
        refreshUser: () => {},
        updateClient: () => {},
        user: user,
        client: user,
        updateUser: userContext.updateUser,
        agency: user.agency,
      }
    : userContext;

  return (
    <SentryRoute
      {...rest}
      render={props => (
        <UserContext.Provider value={contextValue}>
          <ThemeProvider value={{ theme, customizations }}>
            <Component {...rest} {...props} user={user} />
          </ThemeProvider>
        </UserContext.Provider>
      )}
    />
  );
};

// Exposed for SSR render
export const routes = [
  {
    name: 'sentryError',
    path: '/client/sentryError',
    exact: true,
    component: SentryError,
  },
  {
    name: 'Accommodation Profile',
    exact: true,
    path: `/client/example/:savedItineraryId/:agencyId/:agentId/camp/:camp`,
    component: CampPage,
    view: 'campInfo',
  },
  {
    name: 'Enquire V1',
    path: `/client/example/:savedItineraryId/:agencyId/:agentId/enquire`,
    component: Enquire,
    authenticated: false,
  },
  {
    name: 'Enquire V2',
    path: `/client/example/:savedItineraryId/:agencyId/:agentId/enquire2`,
    component: Enquire2,
    authenticated: false,
  },
  {
    name: 'Enquire V3',
    path: `/client/example/:savedItineraryId/:agencyId/:agentId/enquire3`,
    component: Enquire3,
    authenticated: false,
  },
  {
    name: 'Enquire V4',
    path: `/client/example/:savedItineraryId/:agencyId/:agentId/enquire4`,
    component: Enquire4,
    authenticated: false,
  },
  {
    name: 'Questions',
    exact: true,
    path: '/client/itineraries/:id/(questions|faq)',
    component: QuestionsPage,
    view: 'campInfo',
  },
  {
    name: 'Accommodation Profile',
    exact: true,
    path: '/client/itineraries/:id/camp/:camp',
    component: CampPage,
    view: 'campInfo',
  },
  {
    name: 'Client Itinerary Payment Portal',
    authenticated: true,
    exact: true,
    path: '/client/itineraries/:id/pay',
    component: PaymentFlow,
  },
  {
    // Not authenticated: Adyen's returnUrl is built server-side and carries no
    // magic-link ?token=, so this route must mount without the auth guard. It
    // finishes the 3DS authentication and re-appends the stashed token on the way back.
    name: 'Adyen 3DS Return',
    exact: true,
    path: '/client/adyen-3ds',
    component: Adyen3dsReturn,
  },
  {
    name: 'Client Itinerary',
    authenticated: true,
    exact: true,
    path: '/client/itineraries/:id',
    component: ItineraryPage,
  },

  {
    name: 'Client Itineraries',
    authenticated: true,
    exact: true,
    path: '/client/itineraries',
    component: ItinerariesPage,
  },
  {
    name: 'Client Video Share',
    authenticated: false,
    exact: true,
    path: '/client/share/:id',
    component: ShareItinerary,
  },
  {
    name: 'Questions',
    authenticated: false,
    exact: true,
    path: '/client/example/:savedItineraryId/:agencyId/:agentId/questions',
    component: QuestionsPage,
  },
  {
    name: 'Example Itinerary',
    path: `/client/example/:savedItineraryId/:agencyId/:agentId`,
    authenticated: false,
    component: ExampleItineraryPage,
  },
  {
    name: 'Client Login',
    exact: true,
    authenticated: false,
    path: '/client/:route(login|forgot|forgotten|loggedOut|reset)/:token?',
    component: LoginScreen,
  },
  {
    name: 'Country Profile',
    exact: true,
    path: '/client/country/:country',
    component: Country,
  },
  {
    name: 'Region Profile',
    exact: true,
    path: '/client/region/:region',
    component: RegionInfo,
  },
  {
    name: 'Accommodation Profile',
    exact: true,
    path: '/client/camp/:camp',
    component: CampPage,
    view: 'campInfo',
  },
  {
    name: 'Service Detail',
    exact: true,
    path: '/client/activity/:activity',
    component: ActivityInfo,
  },

  {
    name: 'My Bookings',
    authenticated: true,
    exact: true,
    path: '/client/bookings',
    component: BookingsPage,
  },
  {
    name: 'Client Profile',
    authenticated: true,
    exact: true,
    path: '/client/profile',
    component: ProfilePage,
  },
  {
    name: 'Questions',
    authenticated: true,
    exact: true,
    path: '/client/questions',
    component: QuestionsPage,
  },
  {
    name: 'FAQ',
    authenticated: false,
    exact: true,
    path: '/client/faq',
    component: QuestionsPage,
  },
  {
    name: 'activate',
    path: `/client/activate/:token`,
    render: props => (
      <Activate
        {...props}
        onActivate={(...args) => props.Api.verifyToken(...args)}
        onSetClientPassword={(...args) => props.Api.setClientInitialPassword(...args)}
        onSuccess={() => props.history.push(`/client/activated`)}
      />
    ),
  },
  {
    name: 'Client Portal Token Handler',
    path: `/client/portal/:token/:itinerary?/:action?`,
    component: Portal,
    authenticated: false,
  },
  {
    name: 'Client Portal Token Handler',
    path: `/client/portal/:token/itineraries/:itinerary?/:action?`,
    component: Portal,
    authenticated: false,
  },
  {
    name: 'Client Config',
    path: '/config',
    exact: true,
    component: Config,
    authenticated: true,
  },
  {
    name: 'client',
    path: '/client',
    exact: true,
    redirect: '/client/itineraries',
  },
  {
    name: 'root',
    path: '/',
    exact: true,
    redirect: '/client',
  },
  {
    name: 'Client Login',
    path: '/login',
    exact: true,
    redirect: '/client',
  },
];

if (process.env.NODE_ENV === 'development') {
  // Place this before catch all.
  routes.unshift({
    name: 'viewIcons',
    path: '/client/icons',
    component: ViewIcons,
  });
  routes.unshift({
    name: 'viewIcons2',
    path: '/client/icons2',
    component: ViewIcons2,
  });
}

function buildRoute(route, props) {
  let ret;
  if (route.authenticated) {
    ret = <AuthenticatedRoute key={route.name} {...props} {...route} />;
  } else if (route.redirect) {
    ret = <Redirect key={route.name} {...props} to={{ pathname: route.redirect, state: { from: props.location } }} />;
  } else {
    ret = <NonAuthenticatedRoute key={route.name} {...props} {...route} />;
  }
  return ret;
}

const NoMatch = () => {
  const location = useLocation();

  return (
    <div>
      <h3>
        No match for <code>{location.pathname}</code> was found.
      </h3>
      <p>
        <Link to="/">Try going home?</Link>
      </p>
    </div>
  );
};

const Routes = props => {
  return (
    <Content>
      <Suspense fallback={<Loading message="Loading..." />}>
        <Switch>
          {map(routes, route => buildRoute(route, props))}
          <SentryRoute path="*">
            <NoMatch />
          </SentryRoute>
        </Switch>
      </Suspense>
    </Content>
  );
};

export default compose(withApi, withUser)(Routes);
